import { useEffect, useState } from "react";
import { Save, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ImageUpload from "@/components/admin/ImageUpload";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PAGE_LABELS = {
  home: { label: "Home", path: "/" },
  about: { label: "About Us", path: "/about" },
  services: { label: "Services", path: "/services" },
  jobs: { label: "Current Openings (list)", path: "/jobs" },
  "upload-resume": { label: "Upload Resume", path: "/upload-resume" },
  "resume-health-check": { label: "Resume Health Check", path: "/resume-health-check" },
  testimonials: { label: "Testimonials", path: "/testimonials" },
  feedback: { label: "Feedback", path: "/feedback" },
  contact: { label: "Contact Us", path: "/contact" },
  blog: { label: "Career Blog (list)", path: "/blog" },
  privacy: { label: "Privacy Policy", path: "/privacy" },
  terms: { label: "Terms of Use", path: "/terms" },
};

export default function AdminPageSeo() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/admin/page-seo").then((r) => setItems(r.data || []));
  }, []);

  const setField = (slug, patch) => {
    setItems((prev) => prev.map((it) => (it.slug === slug ? { ...it, ...patch } : it)));
  };

  const save = async (slug) => {
    const item = items.find((i) => i.slug === slug);
    if (!item) return;
    setSaving(slug);
    try {
      const payload = {
        title: item.title || "",
        description: item.description || "",
        keywords: item.keywords || "",
        og_image: item.og_image || "",
        no_index: !!item.no_index,
      };
      await api.put(`/admin/page-seo/${slug}`, payload);
      toast.success(`${PAGE_LABELS[slug]?.label || slug} SEO saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-page-seo">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Per-Page SEO</h1>
        <p className="text-sm text-slate-500">
          Override the title / meta description / keywords / social image for each individual page. Leave a field empty to fall back to the global default in <b>Settings → SEO</b>.
        </p>
      </div>

      <Accordion type="multiple" className="space-y-3" data-testid="page-seo-list">
        {items.map((item) => {
          const meta = PAGE_LABELS[item.slug] || { label: item.slug, path: `/${item.slug}` };
          const hasOverride = !!(item.title || item.description || item.keywords || item.og_image);
          return (
            <AccordionItem key={item.slug} value={item.slug} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`page-seo-toggle-${item.slug}`}>
                <div className="flex flex-1 items-center justify-between pr-3 text-left">
                  <div>
                    <div className="font-heading text-base font-semibold text-slate-900">{meta.label}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                      <Globe className="h-3 w-3" /> {meta.path}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasOverride && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Custom</span>}
                    {item.no_index && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">No-index</span>}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t border-slate-100 bg-slate-50/40 px-4 pb-4 pt-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor={`t-${item.slug}`}>Meta title</Label>
                    <Input
                      id={`t-${item.slug}`}
                      value={item.title || ""}
                      onChange={(e) => setField(item.slug, { title: e.target.value })}
                      placeholder="Optional — falls back to the page's default title"
                      data-testid={`page-seo-title-${item.slug}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`d-${item.slug}`}>Meta description</Label>
                    <Textarea
                      id={`d-${item.slug}`}
                      rows={3}
                      value={item.description || ""}
                      onChange={(e) => setField(item.slug, { description: e.target.value })}
                      placeholder="Optional — 150–160 characters recommended"
                      data-testid={`page-seo-desc-${item.slug}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`k-${item.slug}`}>Keywords (comma separated)</Label>
                    <Input
                      id={`k-${item.slug}`}
                      value={item.keywords || ""}
                      onChange={(e) => setField(item.slug, { keywords: e.target.value })}
                      placeholder="pharma jobs, recruitment, ..."
                      data-testid={`page-seo-keywords-${item.slug}`}
                    />
                  </div>
                  <ImageUpload
                    label="Open Graph image"
                    value={item.og_image || ""}
                    onChange={(v) => setField(item.slug, { og_image: v })}
                    folder="og"
                    testId={`page-seo-ogimage-${item.slug}`}
                    helper="Optional — shown when this page is shared on social media."
                  />
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={!!item.no_index}
                      onCheckedChange={(v) => setField(item.slug, { no_index: v })}
                      data-testid={`page-seo-noindex-${item.slug}`}
                    />
                    <span className="text-sm text-slate-700">
                      {item.no_index ? "Excluded from search engines (noindex)" : "Indexed by search engines"}
                    </span>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => save(item.slug)}
                      disabled={saving === item.slug}
                      className="bg-primary hover:bg-primary/90"
                      data-testid={`page-seo-save-${item.slug}`}
                    >
                      <Save className="mr-1 h-4 w-4" />{saving === item.slug ? "Saving…" : "Save this page"}
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
